<template>
  <div v-if="rows.length > 0" class="preview-table">
    <table class="preview-table__table">
      <thead>
        <tr>
          <th class="preview-table__select">
            <ReviewSelectionHeaderCell
              :all-selected="allSelected"
              :indeterminate="indeterminate"
              :disabled="noSelectableRows"
              :label="selectionLabels.selectAll"
              :test-id="TestId.assignSpecialties.selectAll"
              @toggle-all="emit('toggle-all')"
            />
          </th>
          <th class="preview-table__status">
            <!-- The column shows only status icons; the label is for assistive tech. -->
            <span class="preview-table__sr-only">{{ columnLabels.status }}</span>
          </th>
          <th>{{ columnLabels.name }}</th>
          <th>{{ columnLabels.type }}</th>
          <th>{{ columnLabels.resultingSpecialties }}</th>
          <th v-if="clientControlEnabled">{{ columnLabels.control }}</th>
        </tr>
      </thead>
      <tbody>
        <!-- Term names are deduped server-side, so they are a stable row key. -->
        <tr
          v-for="row in visibleItems"
          :key="row.name"
          :class="rowClass(row)"
          :data-testid="TestId.assignSpecialties.previewRow"
        >
          <td class="preview-table__select">
            <!-- Error rows are display-only: no checkbox, just an empty cell. -->
            <ReviewSelectionCell
              v-if="isSelectable(row)"
              :checked="isRowSelected(row)"
              :label="includeRowLabel(row)"
              :test-id="TestId.assignSpecialties.rowCheckbox"
              @toggle="emit('toggle-row', row.name)"
            />
          </td>
          <td class="preview-table__status">
            <RowStatusIcon :status="iconStatus(row)" :label="statusLabel(row)" />
          </td>
          <td>{{ row.name }}</td>
          <td>{{ row.type }}</td>
          <td>
            <!-- The column header already names the attribute, so the diff renders
                 without per-attribute labels. -->
            <ChangeSetDiff :changeset="specialtyChangeset(row)" :labels="diffLabels" :show-attribute-labels="false" />
          </td>
          <td v-if="clientControlEnabled" :data-testid="TestId.assignSpecialties.flagOutcome">
            {{ outcomeText(row) }}
          </td>
        </tr>
      </tbody>
    </table>

    <TableFooter
      v-if="showPager"
      class="preview-table__footer"
      variant="steps"
      :page="page"
      :page-count="pageCount"
      :per-page="perPage"
      :per-page-choices="perPageChoices"
      :is-first-page="isFirstPage"
      :is-last-page="isLastPage"
      @page-change="handlePageChange"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { TestId } from "#/test-ids";
import ChangeSetDiff from "#ce/apps/SpAdmin/shared/components/ChangeSetDiff.ce.vue";
import ReviewSelectionCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionCell.ce.vue";
import ReviewSelectionHeaderCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionHeaderCell.ce.vue";
import RowStatusIcon from "#ce/apps/SpAdmin/shared/components/RowStatusIcon.ce.vue";
import TableFooter from "#ce/components/SpTable/components/TableFooter.ce.vue";
import { useTablePagination } from "#ce/components/SpTable/composables/useTablePagination";

const PER_PAGE_OPTIONS = [50, 100, 250];

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  // Selection state comes from the store's row selection (keyed by row name);
  // this component stays presentational and emits toggles back up.
  selectedKeys: {
    type: Array,
    default: () => [],
  },
  allSelected: {
    type: Boolean,
    default: false,
  },
  indeterminate: {
    type: Boolean,
    default: false,
  },
  outcomeLabels: {
    type: Object,
    default: () => ({}),
  },
  statusLabels: {
    type: Object,
    default: () => ({}),
  },
  // The store's merged `labels` object; this component reads `columns` and
  // `invalidEntry` from it.
  labels: {
    type: Object,
    default: () => ({}),
  },
  clientControlEnabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["toggle-row", "toggle-all"]);

const columnLabels = computed(() => props.labels.columns ?? {});

const selectionLabels = computed(() => props.labels.selection ?? {});

const diffLabels = computed(() => ({ removedValue: props.labels.removedSpecialty }));

// Only the rendering is paged. `ConfirmStep`'s summary above the table counts every row.
const requestedPage = ref(1);
const requestedPerPage = ref(PER_PAGE_OPTIONS[1]);
const { page, perPage, pageCount, isPaginated, isFirstPage, isLastPage, visibleItems, perPageChoices } =
  useTablePagination({
    items: () => props.rows,
    page: requestedPage,
    perPage: requestedPerPage,
    clientPaginate: true,
    perPageOptions: PER_PAGE_OPTIONS,
  });
// Hidden while every page size would render the same rows.
const showPager = computed(() => isPaginated.value && props.rows.length > PER_PAGE_OPTIONS[0]);

// A new preview replaces the rows, so the view returns to its first page.
function resetPage() {
  requestedPage.value = 1;
}

watch(() => props.rows, resetPage);

function handlePageChange({ page: nextPage, perPage: nextPerPage }) {
  requestedPerPage.value = nextPerPage;
  requestedPage.value = nextPage;
}

const noSelectableRows = computed(() => !props.rows.some(isSelectable));

function isSelectable(row) {
  return row.status !== "error";
}

// Set lookup keeps a render O(rows).
const selectedKeySet = computed(() => new Set(props.selectedKeys));

function isRowSelected(row) {
  return selectedKeySet.value.has(row.name);
}

function includeRowLabel(row) {
  return String(selectionLabels.value.includeRow ?? "").replaceAll("%{name}", row.name);
}

function specialtyChangeset(row) {
  return { specialties: [row.current_specialties ?? [], row.resulting_specialties ?? []] };
}

function iconStatus(row) {
  return ["new", "existing"].includes(row.status) ? row.status : "error";
}

function statusLabel(row) {
  if (row.status === "new") {
    return props.statusLabels.new;
  }

  if (row.status === "existing") {
    return props.statusLabels.existing;
  }

  return rowErrorText(row);
}

function rowClass(row) {
  if (row.status === "existing") {
    return "preview-table__row--existing";
  }

  if (row.status === "error") {
    return "preview-table__row--error";
  }

  return "";
}

function rowErrorText(row) {
  return row.errors?.join(", ") || props.labels.invalidEntry || "";
}

// New terms are created client-owned through the request workflow, so they show the
// new_term label regardless of outcome. Existing rows only call out the will_enable
// transition; every other outcome (already_enabled, not_needed, or absent when the
// control checkbox is off) reads as "Unchanged".
function outcomeText(row) {
  if (row.status === "new") {
    return props.outcomeLabels.new_term || "";
  }

  if (row.client_specialties_outcome === "will_enable") {
    return props.outcomeLabels.will_enable || "";
  }

  return props.outcomeLabels.unchanged || "";
}
</script>

<style scoped lang="scss">
.preview-table {
  // The pager reads SpTable's header-colour alias through inheritance.
  --table-header-color: var(--sp-comp-table-header-color, var(--sp-sys-color-on-surface-variant));

  // Use the remaining modal height. When the rows need more room, scroll this
  // wrapper so the sticky header and pager stay attached to the table.
  flex: 0 1 auto;
  min-height: var(--sp-ref-scale-48);
  overflow-y: auto;
  border: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant, #f9f9f9);
  border-radius: var(--sp-ref-scale-4);

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);

    th,
    td {
      padding: var(--sp-ref-scale-8);
      text-align: left;
      border-bottom: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant, #f9f9f9);
    }

    th {
      font-size: var(--sp-ref-font-size-small, 0.625rem);
      font-weight: var(--sp-ref-font-weight-normal, 400);
      letter-spacing: 0.5px;
      color: var(--sp-sys-color-on-surface-variant, #4a4a4a);
      background-color: var(--sp-sys-color-surface);
      position: sticky;
      top: 0;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:nth-child(even) {
      background-color: var(--sp-sys-color-surface-variant, #f9f9f9);
    }
  }

  &__select {
    width: var(--sp-ref-scale-32);
    text-align: center;
  }

  &__status {
    width: 2rem;
    text-align: center;
  }

  &__footer {
    position: sticky;
    bottom: 0;
    background-color: var(--sp-sys-color-surface);
    border-top: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant);
  }

  // Screen-reader-only text for the status column's assistive label.
  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
